import { IdAutoCapture } from '../components/common/id-auto-capture/id-auto-capture';
import { LoggerService } from './LoggerService';
import * as tf from '@tensorflow/tfjs';

interface DetectedObject {
  bbox: [number, number, number, number]; // [x, y, width, height]
  class: string;
  score: number;
}

export class DetectionHelpers {
  public static async detectIdWithTensorFlow(detectionModel: any, sourceImg: HTMLVideoElement, minConfidence: number = 0.9): Promise<DetectedObject | null> {
    if (!detectionModel) {
      LoggerService.warn('Detection model not provided');
      return null;
    }

    // Check if video is ready
    if (!sourceImg || sourceImg.readyState < 2) {
      LoggerService.warn('Video element not ready');
      return null;
    }

    const modelInputSize: [number, number] = [320, 320];

    // Create input tensor
    const inputTensor = tf.tidy(() => {
      return tf.browser.fromPixels(sourceImg).resizeBilinear(modelInputSize).expandDims(0);
    });

    let bestPrediction: DetectedObject | null = null;
    let maxArea = 0;

    // Track all tensors for cleanup
    let rawOutput: tf.Tensor | tf.Tensor[] | null = null;
    let nmsIndicesTensor: tf.Tensor1D | null = null;

    try {
      // Run model inference
      rawOutput = await detectionModel.executeAsync(inputTensor);

      // Ensure rawOutput is an array of Tensors
      const outputTensors = Array.isArray(rawOutput) ? rawOutput : [rawOutput];

      if (outputTensors.length < 3) {
        LoggerService.error('Model returned insufficient outputs. Expected at least 3 tensors.');
        return null;
      }

      // Log tensor shapes for debugging
      outputTensors.forEach((t, i) => LoggerService.log(`Output Tensor ${i}: shape=${t.shape}, dtype=${t.dtype}`));

      const classesTensor = outputTensors[0];
      const scoresTensor = outputTensors[1];
      const boxesTensor = outputTensors[2];

      // Validate tensors
      if (!boxesTensor || !scoresTensor || !classesTensor) {
        LoggerService.error('Missing one or more expected output tensors (boxes, scores, classes).');
        return null;
      }

      // Convert tensors to arrays
      const boxesRaw = await boxesTensor.array();
      const scoresRaw = await scoresTensor.array();
      const classesRaw = await classesTensor.array();

      // Ensure scores and classes are 1D arrays
      const scores: number[] = Array.isArray(scoresRaw) ? (scoresRaw as number[]).flat() : [scoresRaw as number];

      const classes: number[] = Array.isArray(classesRaw) ? (classesRaw as number[]).flat() : [classesRaw as number];

      // Ensure boxes is a 2D array
      const boxes: number[][] = boxesRaw as number[][];

      // Validate array lengths
      if (boxes.length !== scores.length || scores.length !== classes.length) {
        LoggerService.error('Mismatch in output array lengths:', {
          boxes: boxes.length,
          scores: scores.length,
          classes: classes.length,
        });
        return null;
      }

      // Log for debugging
      LoggerService.log('Processed arrays:', {
        boxesCount: boxes.length,
        scoresCount: scores.length,
        classesCount: classes.length,
      });

      // Filter detections by confidence threshold
      const filteredIndices = scores.map((score, idx) => (score > minConfidence ? idx : -1)).filter(idx => idx !== -1);

      if (filteredIndices.length === 0) {
        LoggerService.log('No detections above confidence threshold');
        return null;
      }

      const filteredBoxes = filteredIndices.map(idx => boxes[idx]);
      const filteredScores = filteredIndices.map(idx => scores[idx]);
      const filteredClasses = filteredIndices.map(idx => classes[idx]);

      LoggerService.log(`Found ${filteredIndices.length} detections above threshold`);

      // Apply Non-Maximum Suppression (NMS)
      // IMPORTANT: Create tensors inside try-catch for proper cleanup
      const boxesTensor2d = tf.tensor2d(filteredBoxes);
      const scoresTensor1d = tf.tensor1d(filteredScores);

      try {
        nmsIndicesTensor = await tf.image.nonMaxSuppressionAsync(
          boxesTensor2d,
          scoresTensor1d,
          100, // max_output_size
          0.5, // iou_threshold
          minConfidence, // score_threshold
        );

        const nmsIndices = (await nmsIndicesTensor.array()) as number[];

        LoggerService.log(`After NMS: ${nmsIndices.length} detections`);

        // Define target ID classes
        const targetIdClasses = ['DL', 'TD1_BACK', 'TD1_FRONT', 'TD2', 'TD3'];

        // Process each detection after NMS
        for (const i of nmsIndices) {
          const bbox = filteredBoxes[i]; // [x1, y1, x2, y2]
          const score = filteredScores[i];
          const classId = filteredClasses[i];

          // Get class name
          const detectedClass = IdAutoCapture.CLASS_NAMES[classId];

          if (!detectedClass) {
            LoggerService.warn(`Unknown class ID: ${classId}`);
            continue;
          }

          // Check if this is a target ID class
          if (!targetIdClasses.includes(detectedClass)) {
            continue;
          }

          // Convert normalized coordinates [0-1] to pixel coordinates
          // and convert [x1, y1, x2, y2] to [x, y, width, height]
          const x1 = bbox[0] * sourceImg.videoWidth;
          const y1 = bbox[1] * sourceImg.videoHeight;
          const x2 = bbox[2] * sourceImg.videoWidth;
          const y2 = bbox[3] * sourceImg.videoHeight;

          const x = x1;
          const y = y1;
          const width = x2 - x1;
          const height = y2 - y1;

          // Validate bounding box dimensions
          if (width <= 0 || height <= 0) {
            LoggerService.warn('Invalid bounding box dimensions:', { width, height });
            continue;
          }

          // Calculate area and keep the largest detection
          const area = width * height;
          if (area > maxArea) {
            maxArea = area;
            bestPrediction = {
              bbox: [x, y, width, height],
              class: detectedClass,
              score: score,
            };
          }
        }
      } finally {
        // Clean up NMS tensors
        boxesTensor2d.dispose();
        scoresTensor1d.dispose();
      }
    } catch (error) {
      LoggerService.error('TensorFlow.js detection error:', error);
      return null;
    } finally {
      // CRITICAL: Clean up all tensors
      inputTensor.dispose();

      if (rawOutput) {
        if (Array.isArray(rawOutput)) {
          rawOutput.forEach(t => {
            if (t && !t.isDisposed) {
              t.dispose();
            }
          });
        } else {
          if (!rawOutput.isDisposed) {
            rawOutput.dispose();
          }
        }
      }

      if (nmsIndicesTensor && !nmsIndicesTensor.isDisposed) {
        nmsIndicesTensor.dispose();
      }
    }

    if (bestPrediction) {
      LoggerService.log('Best detection:', {
        class: bestPrediction.class,
        score: bestPrediction.score.toFixed(3),
        area: maxArea.toFixed(0),
      });
    }

    return bestPrediction;
  }

  /**
   * Helper method to check tensor memory health
   */
  public static checkMemoryHealth(): void {
    const memory = tf.memory();

    LoggerService.log('TensorFlow Memory:', {
      numTensors: memory.numTensors,
      numBytes: `${(memory.numBytes / 1024 / 1024).toFixed(2)} MB`,
      numDataBuffers: memory.numDataBuffers,
    });

    // Warn if tensor count is high
    if (memory.numTensors > 50) {
      LoggerService.warn('⚠️ High tensor count - possible memory leak');
    }
  }

  public static async detectIdBatch(detectionModel: any, frames: HTMLVideoElement[], minConfidence: number = 0.9): Promise<(DetectedObject | null)[]> {
    const results = await Promise.all(frames.map(frame => this.detectIdWithTensorFlow(detectionModel, frame, minConfidence)));

    return results;
  }
}
